import * as React from 'react';
import { Dispatch } from 'react-redux';
import { Api } from '../typings/api';
import store from '../store';
import { StoreState } from '../reducers';
import NativeApi from './nativeApi';
import UrlUtils, { ApiUrls } from './url';
import UserAgentUtils from './userAgent';
import { AppType, AppInfosState } from '../containers/App/reducer';
import { GalleryInfosState } from '../containers/Gallery/reducer';
import { DriveListState, DriveItemInfo, FetchStatus } from '../containers/DriveItemList/reducer';
import { AuthenticationUserState } from '../containers/Authentication/reducer';
import { DownloadStatus, OfflineManagerState, OfflineManagerStatus, ThumbMap } from '../containers/OfflineManager/reducer';
import { RoutePath, RouteParams, RouteParamsDriveItemListPage, RouteParamsDriveItemDetailPage, RouteParamsTheater, RouteParamsStaticPage } from '../routes/constants';
import { DriveItemListOrigin } from '../containers/DriveItemListPage/constants';
import { getOrFetchDriveItemMeta } from '../containers/DriveItemList/actions';
import DriveItemUtils from './driveItem';
import ApiUtils from './api';
import AuthUtils from './authentication';
import { MultiAppAuthenticationCredentials } from '../containers/MultiAppAuthentication/reducer';
import { checkActiveSession, orgLogin, freeUrlAppLogin } from '../containers/MultiAppAuthentication/actions';
import { AuthMethod } from '../containers/MultiAppAuthentication/constants';
import { ThumbType, LogoType } from '../containers/OfflineManager/reducer';
import { MenuItemType } from '../typings/menu';
import { RouterParamsState } from '../containers/SetRouteParams/reducer';
import { throttle } from './optimization';
import { getIsAccountManagerOrAdmin } from '../containers/ProfilePage/selectors';
import { getProfileData } from '../containers/ProfilePage/actions';
import { showLockedEditorPopup } from '../containers/Editor/actions';

const defaultBrandIcon = require('../icons/webpublication.svg');
const defaultMultiAppBarIcon = require('../icons/uprez_bar.svg');
const defaultMultiAppIcon = require('../icons/uprez.svg');

const I18n = require('react-redux-i18n').I18n;

export interface HttpHeader {
	key: string;
	value: string;
}

export interface GalleryCredentials {
	login: string;
	password: string;
}

export enum ScreenFormat {
	Tablet = 'tablet',
	Mobile = 'mobile',
	Desktop = 'desktop'
}

export enum ServerStatusCode {
	OK = 200,
	NO_CONTENT = 204,
	UNAUTHORIZED = 401,
	FORBIDDEN = 403,
	CONFLICT = 409,
	LOCKED = 423,
}

enum HttpHeaderType {
	CONTENT_TYPE = 'Content-Type',
	LOCATION = 'Location',
}

export enum HttpContentType {
	JSON = 'application/json; charset=utf-8',
	URLENCODED = 'application/x-www-form-urlencoded',
}

export const downloadsFolder = 'downloads';
export const tempFolder = 'tmp';
export const thumbnailsFolder = 'thumbnails';
export const iconsFolder = 'icons';
export const openedFilesFolder = 'openedFiles';
export const defaultFolderIconFolder = 'defaultFolderIcon';
export const bgMediaFolder = 'bgMedia';
const MAX_RETRIES = 3;

export default class Utils {
	static UPLOAD_PROGRESS_DELAY: number = 1000;
	static deviceRootPath = window.location.origin + window.location.pathname.replace('indexConteneur.html', '') + '../..';
	static cancelableRequests: { [k: string]: XMLHttpRequest } = {};

	static isGalleryReady = (galleryInfos: GalleryInfosState, offlineManager: OfflineManagerState, appInfos: AppInfosState): boolean => {
		const isConfigAndTemplateReady = galleryInfos.isConfigLoaded && Utils.isTemplateLoaded(appInfos, galleryInfos);
		const isOnline = UserAgentUtils.isOnline();
		const isMobileApp = UserAgentUtils.isMobileApp;

		// In mobile app mode when online, allow content to display before full initialization completes
		// This allows the app to start faster while content structure is prepared in the background
		// However, we still need the root gallery item to be loaded to avoid errors
		if (isMobileApp && isOnline) {
			const { driveList } = store.getState();
			const rootGalleryId = galleryInfos.config && galleryInfos.config.applicationGallery;
			const isRootGalleryLoaded = rootGalleryId != null && !!driveList.driveItems[rootGalleryId];
			return isConfigAndTemplateReady && isRootGalleryLoaded;
		}

		// In browser mode or offline mode, require full initialization
		return isConfigAndTemplateReady && offlineManager.status === OfflineManagerStatus.INITIALIZED;
	}

	static getGalleryName = async (config: Api.Config, driveList: DriveListState, dispatch: Dispatch<StoreState>): Promise<string> => {
		if (!driveList.driveItems[config.applicationGallery]) {
			if (Utils.shouldGetProfileData()) {
				await dispatch(getProfileData(config.clientId));
			}

			dispatch(getOrFetchDriveItemMeta(config.applicationGallery));
			return '';
		}
		return I18n.t('page_title.gallery_name', { galleryName: DriveItemUtils.getDisplayName(driveList.driveItems[config.applicationGallery])});
	}

	static isTemplateLoaded = (appInfos: AppInfosState, galleryInfos: GalleryInfosState): boolean => {
		const { isLocalTemplateLoaded, isRemoteTemplateLoaded } = galleryInfos;
		const isMobileMultiApp = Utils.isMobileMultiApp(appInfos);
		return isMobileMultiApp ? isRemoteTemplateLoaded : isLocalTemplateLoaded;
	}

	static getItemName = (driveList: DriveListState, id: number, dispatch: Dispatch<StoreState>): string => {
		if (!driveList.driveItems[id]) {
			dispatch(getOrFetchDriveItemMeta(id));
			return '';
		}
		return DriveItemUtils.getDisplayName(driveList.driveItems[id]);
	}

	static getPageTitle = async (route: RoutePath, params: RouteParams, config: Api.Config, driveList: DriveListState, dispatch: Dispatch<StoreState>): Promise<string> => {
		const galleryName = await Utils.getGalleryName(config, driveList, dispatch);
		let castedParams;
		let itemName;

		if (!galleryName) {
			return '';
		}

		// All RoutePath must be in the switch, do not add a default case
		switch (route) {
			case RoutePath.Default:
			case RoutePath.Home:
				return galleryName;
			case RoutePath.ViewDriveItem:
				return I18n.t('page_title.view');
			case RoutePath.Authentication:
				return I18n.t('page_title.authentication');
			case RoutePath.ParametersPage:
				return I18n.t('page_title.parameters', { galleryName });
			case RoutePath.ProfilePage:
				return I18n.t('page_title.profile');
			case RoutePath.ProposalStatsPage:
				return I18n.t('page_title.my_sent');
			case RoutePath.DriveItemListPage:
				castedParams = params as RouteParamsDriveItemListPage;
				return Utils.getDriveItemListPageTitle(castedParams, galleryName, driveList, dispatch);
			case RoutePath.Theater:
			case RoutePath.TheaterList:
				castedParams = params as RouteParamsTheater;
				itemName = driveList && castedParams.driveItemId ? Utils.getItemName(driveList, castedParams.driveItemId, dispatch) : '';
				return itemName ? I18n.t('page_title.theater', { galleryName, itemName }) : '';
			case RoutePath.DriveItemDetailPage:
				castedParams = params as RouteParamsDriveItemDetailPage;
				itemName = driveList && castedParams.driveItemId ? Utils.getItemName(driveList, castedParams.driveItemId, dispatch) : '';
				return itemName ? I18n.t('page_title.details', { galleryName, itemName }) : '';
			case RoutePath.NotFound:
				return I18n.t('messages.page_not_found');
			case RoutePath.StaticPage:
				castedParams = params as RouteParamsStaticPage;
				itemName = driveList && castedParams.component ? Utils.getItemName(driveList, parseInt(castedParams.component, 10), dispatch) : '';
				return `${itemName} - ${galleryName}`;
			case RoutePath.Editor:
				return I18n.t('page_title.editor');
			case RoutePath.NotesPage:
				return I18n.t('page_title.my_notes');
			case RoutePath.SharePage:
				return I18n.t('page_title.share');
		}
	}

	static getDriveItemListPageTitle = (params: RouteParamsDriveItemListPage, galleryName: string, driveList, dispatch): string => {
		const origin: DriveItemListOrigin = params.origin;

		switch (origin) {
			case DriveItemListOrigin.Downloads:
				return I18n.t('page_title.downloads', { galleryName });
			case DriveItemListOrigin.Selection:
				return I18n.t('page_title.selection', {galleryName});
			case DriveItemListOrigin.Folder: {
				if (!params.originParam) {
					return I18n.t('page_title.drive', { galleryName });
				}
				const itemName = driveList && params.originParam ? Utils.getItemName(driveList, parseInt(params.originParam, 10), dispatch) : '';
				return itemName ? I18n.t('page_title.folder', { galleryName, itemName }) : '';
			}
			case DriveItemListOrigin.New:
				return I18n.t('page_title.new', { galleryName });
			case DriveItemListOrigin.Search:
				return I18n.t('page_title.search', { galleryName });
			case DriveItemListOrigin.Assortment:
				return I18n.t('page_title.assortment', { galleryName });
		}
	}

	static getBrandLogoName = (template: Api.Template, logoType: LogoType): string => {
		return template.brand && template.brand[logoType] ? template.brand[logoType] : '';
	}

	static getDefaultLogo = (isHome: boolean, isMultiApp: boolean): string => {
		if (isMultiApp) {
			return isHome ? defaultMultiAppIcon : defaultMultiAppBarIcon;
		}

		return defaultBrandIcon;
	}

	static getOriginBrandLogoUrl = (galleryInfos: GalleryInfosState, isHome: boolean, isMultiApp: boolean, template?: Api.Template): string => {
		const resolvedTemplate = template || galleryInfos.template;
		const logoType = isHome ? LogoType.HOME_LOGO : LogoType.BRAND_LOGO;

		if (resolvedTemplate) {
			if (resolvedTemplate.brand && resolvedTemplate.brand[logoType]) {
				const brandLogo = resolvedTemplate.brand[logoType];
				const brandLogoFilename = Utils.getFilenameFromMediaId(brandLogo);
				return UrlUtils.getItemOriginalFileUrl(galleryInfos.config, brandLogoFilename);
			}
			return Utils.getDefaultLogo(isHome, isMultiApp);
		}
		return '';
	}

	static isMaster = (galleryConfig: Api.Config): boolean => {
		return galleryConfig.mediaPath !== undefined;
	}

	static getComponentUrl = (galleryInfos: GalleryInfosState, componentId: number, isAvailableOffline: boolean, locale: string): string => {
		let componentURL = UrlUtils.getComponentUrl(galleryInfos, componentId);

		if (UserAgentUtils.isMobileApp && isAvailableOffline) {
			componentURL = `${Utils.getPersistentDownloadsFolder()}/${componentId}/index.html`;
		}

		const separator = Utils.getUrlSeparator(componentURL);

		return `${componentURL}${separator}locale=${locale}`;
	}

	static getGalleryCredentials = (userInfos?: AuthenticationUserState): GalleryCredentials | null => {
		if (userInfos === undefined) {
			const storeState = store.getState();
			userInfos = storeState.authentication.userInfos;
		}
		const { mode } = userInfos;
		const isConnectedExcel = AuthUtils.isConnected(mode) && AuthUtils.isExcel(mode);

		if (userInfos.needCredentials && isConnectedExcel) {
			return {
				login: userInfos.login,
				password: userInfos.password,
			};
		} else {
			return null;
		}
	}

	static getMultiAppCredentials = (): MultiAppAuthenticationCredentials | undefined => {
		const storeState = store.getState();
		return storeState.multiAppAuthenticationInfos.credentials;
	}

	static getWelcomePageDownloadUrl = (galleryInfos: GalleryInfosState, template?: Api.Template): string => {
		const resolvedTemplate = template || galleryInfos.template;

		if (resolvedTemplate && resolvedTemplate.welcomepage) {
			const welcomePageUrl = resolvedTemplate.welcomepage.url;
			const welcomePageId = Utils.getMediaId(welcomePageUrl);
			return Utils.getComponentDownloadUrlById(galleryInfos, welcomePageId);
		}
		return '';
	}

	static getComponentDownloadUrl = (galleryInfos: GalleryInfosState, component: Api.DriveItem): string => {
		const componentId = component.id;
		return `${Utils.getComponentDownloadUrlById(galleryInfos, componentId)}`;
	}

	static getComponentDownloadUrlById = (galleryInfos: GalleryInfosState, componentId: number): string => {
		return `${UrlUtils.getResourceZipUrl(galleryInfos.config, componentId)}`;
	}

	static getMediaId(item: string): number {
		const regexMatches = /^\$\{media\}(.+)$/.exec(item);
		return regexMatches !== null ? parseInt(regexMatches[1], 10) : -1;
	}

	static getFilenameFromMediaId(mediaId: string): string {
		const filenameResult = /^\$media\$(.+)$/.exec(mediaId);
		return filenameResult ? filenameResult[1] : '';
	}

	static getBrandLogoUrl = (galleryInfos: GalleryInfosState, offlineManager: OfflineManagerState, isHome: boolean, isMultiApp: boolean): string => {
		if (UserAgentUtils.isMobileApp) {
			const thumbType = isHome ? ThumbType.HOME_THUMB : ThumbType.BRAND_THUMB;
			const logoType = isHome ? LogoType.HOME_LOGO : LogoType.BRAND_LOGO;

			const { remoteConfig, localConfig } = offlineManager[thumbType];

			if (galleryInfos.isRemoteTemplateLoaded && remoteConfig.filename && remoteConfig.status === DownloadStatus.DOWNLOADED) {
				return `${Utils.getThumbnailsFolder()}/brand/${Utils.getFilenameFromMediaId(remoteConfig.filename)}`;
			} else if (!remoteConfig.filename && localConfig.filename && localConfig.status === DownloadStatus.DOWNLOADED) {
				return `${Utils.getThumbnailsFolder()}/brand/${Utils.getFilenameFromMediaId(localConfig.filename)}`;
			}

			if (galleryInfos.isRemoteTemplateLoaded && !Utils.getBrandLogoName(galleryInfos.template, logoType)) {
				return Utils.getDefaultLogo(isHome, isMultiApp); // if no logo has been set, we use the webpublication one
			}
			return ''; // if logo is downloading, nothing is rendered to avoid having several logos shown
		}
		return Utils.getOriginBrandLogoUrl(galleryInfos, isHome, isMultiApp);
	}

	static getDeviceThumbnailPath = (galleryConfig: Api.Config, thumbMap: ThumbMap, driveItem: Api.DriveItem, resolutions: { [k: string]: Api.ResolutionGroup }, isIcon: boolean) => {
		const thumbPath = isIcon ? DriveItemUtils.getIconOriginalPath(driveItem, galleryConfig) : DriveItemUtils.getThumbnail(driveItem, galleryConfig, resolutions, true);
		const extension = Utils.getFileExtension(thumbPath);

		if (isIcon) {
			return `${Utils.getIconsFolder()}/${driveItem.id}.${extension}`;
		}

		const thumbData = thumbMap[driveItem.id];
		const hasFileName = thumbData && thumbData.fileName;
		if (hasFileName) {
			// Validate that the stored filename has the correct extension
			// This handles cases where thumbnails were downloaded with wrong extensions (e.g., .mp4 instead of .webp for videos)
			const storedExtension = Utils.getFileExtension(thumbData.fileName);
			if (storedExtension !== extension && extension) {
				console.warn(`[getDeviceThumbnailPath] Stored thumbnail filename has wrong extension for item ${driveItem.id}.
					Expected: ${extension}, Found: ${storedExtension}. File: ${thumbData.fileName}`);
				// Use the correct extension from the thumbnail path instead of the stored filename
				return `${Utils.getThumbnailsFolder()}/${driveItem.id}/card_thumbnail.${extension}?version=${thumbData.version}`;
			}
			return `${Utils.getThumbnailsFolder()}/${driveItem.id}/${thumbData.fileName}?version=${thumbData.version}`;
		}

		// This fallback is for the thumbnails downloaded with Uprez version 3.56 or less. Because filename was not stored at the time.
		return `${Utils.getThumbnailsFolder()}/${driveItem.id}/card_thumbnail.${extension || 'jpg'}?version=${thumbData.version}`;
	}

	static getCustomIconUrl = (galleryConfig: Api.Config, thumbMap: ThumbMap, driveItem: Api.DriveItem, resolutions: { [k: string]: Api.ResolutionGroup }): string => {
		if (UserAgentUtils.isMobileApp) {
			return `${Utils.getDeviceThumbnailPath(galleryConfig, thumbMap, driveItem, resolutions, true)}`;
		}
		return UrlUtils.getItemOriginalFileUrl(galleryConfig, driveItem.originalFile);
	}

	static getPersistentDownloadsFolder = (): string => {
		return `${Utils.deviceRootPath}/${downloadsFolder}`;
	}

	static getRelativeDownloadsFolder = (): string => {
		return downloadsFolder;
	}

	static getTemporaryDownloadsFolder = (): string => {
		return `${Utils.deviceRootPath}/${tempFolder}`;
	}

	static getThumbnailsFolder = (): string => {
		return `${Utils.deviceRootPath}/${thumbnailsFolder}`;
	}

	static getIconsFolder = (): string => {
		return `${Utils.deviceRootPath}/${iconsFolder}`;
	}

	static getBgMediaFolder = (): string => {
		return `${Utils.deviceRootPath}/${bgMediaFolder}`;
	}

	static getPersistentOpenedFilesFolder = (): string => {
		return `${Utils.deviceRootPath}/${openedFilesFolder}`;
	}

	static getRelativeOpenedFilesFolder = (): string => {
		return openedFilesFolder;
	}

	static formatFileSize(size: number): string {
		const units = ['o', 'Ko', 'Mo', 'Go', 'To'];
		let tmpSize = size;
		let i;

		for (i = 0; i < units.length; i++) {
			if (tmpSize < 1024) {
				return `${tmpSize.toFixed(1)} ${units[i]}`;
			}
			tmpSize = tmpSize / 1024;
		}
		return `${tmpSize.toFixed(1)} ${units[i]}`;
	}

	// Only works for pure Json object (functions & dates do not get copied properly)
	static deepCopy = <T>(source: T): T => {
		return JSON.parse(JSON.stringify(source));
	}

	static getSvgIcon(iconFileName: string, attributes: React.SVGProps<SVGUseElement> = {}): React.ReactNode {
		const useNode = React.createElement('use', { xlinkHref: `#svgicon-${iconFileName}`});
		return React.createElement('svg', attributes, useNode);
	}

	static nl2br(str: string): string {
		return str.replace(/(?:\r\n|\r|\n)/g, '<br />');
	}

	static promiseXHR(
		protocol: string,
		url: string,
		headers: HttpHeader[] = [],
		isAsync: boolean = true,
		dataInUrl: {} | null = null,
		dataInBody: BodyInit | undefined = undefined,
		attempt: number = 0,
		ignoreConnectionStatus: boolean = false,
		withCredentials: boolean = true,
		preventRedirect: boolean = true,
		onProgress?: (e: ProgressEvent) => void,
		requestId?: string,
	): Promise<XMLHttpRequest> {
		return new Promise<XMLHttpRequest>((resolve, reject) => {
			const isOnline = UserAgentUtils.isOnline();
			if (ignoreConnectionStatus === false && isOnline !== true) {
				if (isOnline === false) {
					console.warn('xhr rejected: connection status is false', url);
					reject();
				}
				console.warn('xhr rejected: connection status not initialised', url);
				reject();
			}

			let urlWithParams = url;
			let payload = dataInBody;

			if (dataInUrl && !Utils.isEmptyObject(dataInUrl)) {
				urlWithParams += Utils.getUrlSeparator(urlWithParams) + Utils.dataToUrl(dataInUrl);
			}

			if (preventRedirect) {
				urlWithParams += Utils.getUrlSeparator(urlWithParams) + 'preventRedirect=true';
			}

			const xhr = new XMLHttpRequest();

			if (requestId) {
				Utils.cancelableRequests[requestId] = xhr;
			}

			xhr.withCredentials = withCredentials;
			xhr.open(protocol, urlWithParams, isAsync);

			headers.forEach(header => {
				if (header.key === 'overrideMimeType') {
					xhr.overrideMimeType(header.value);
				} else {
					xhr.setRequestHeader(header.key, header.value);
				}
			});

			xhr.onload = async () => {
				const { status, responseURL } = xhr;
				const isForbiddenRequest = [
					ServerStatusCode.UNAUTHORIZED,
					ServerStatusCode.FORBIDDEN,
				].includes(status) && !responseURL.includes('logout');

				if (isForbiddenRequest) {
					return Utils.handleForbiddenRequest(
						xhr, resolve, reject, protocol, url, headers, isAsync, dataInUrl, dataInBody, attempt, ignoreConnectionStatus, withCredentials, preventRedirect, onProgress, requestId,
					);
				}

				const isLockedResource = status === ServerStatusCode.LOCKED;

				if (isLockedResource) {
					let userName = '';

					try {
						const { message } = JSON.parse(xhr.responseText);
						userName = message.split('--')[1];
					// tslint:disable-next-line
					} catch (error) {}

					store.dispatch(showLockedEditorPopup(userName));
					return;
				}

				resolve(xhr);

				if (requestId) {
					delete Utils.cancelableRequests[requestId];
				}

				const URLS_WITHOUT_SESSION_CHECKING = [
					`${ApiUrls.AUTH}refresh`,
					`${ApiUrls.AUTH}check`,
					`${ApiUrls.AUTH}logout`,
					UserAgentUtils.checkOnlineStatusDomain,
				];
				const shouldCheckSession = URLS_WITHOUT_SESSION_CHECKING.every(urlWithoutSessionChecking => !url.includes(urlWithoutSessionChecking));

				if (shouldCheckSession) {
					await store.dispatch(checkActiveSession());
				}
			};

			xhr.onabort = () => {
				reject(xhr);

				if (requestId) {
					delete Utils.cancelableRequests[requestId];
				}
			};

			// The hack resolving the promise if (xhr.readyState === 4 && xhr.status === 0) has been deleted, it may be useful for cross domain requests
			xhr.onerror = () => {
				const isNativeApiCall = url.includes(NativeApi.targetUrl);
				if (isNativeApiCall) {
					Utils.handleNativeApiBug(
						xhr, resolve, reject,
						protocol, url, headers, isAsync, dataInUrl, dataInBody, attempt, ignoreConnectionStatus, withCredentials, preventRedirect, onProgress, requestId
					);

					return;
				}

				reject(xhr);

				if (requestId) {
					delete Utils.cancelableRequests[requestId];
				}
			};

			if (onProgress) {
				xhr.upload.onprogress = throttle(onProgress, Utils.UPLOAD_PROGRESS_DELAY);
			}

			xhr.send(payload);
		});
	}

	static async handleForbiddenRequest(
		xhr: XMLHttpRequest,
		resolve: (value?: XMLHttpRequest | PromiseLike<XMLHttpRequest> | undefined) => void,
		reject: (reason?: {}) => void,
		protocol: string,
		url: string,
		headers: HttpHeader[],
		isAsync: boolean,
		dataInUrl: {} | null,
		dataInBody: BodyInit | undefined,
		attempt: number,
		ignoreConnectionStatus: boolean,
		withCredentials: boolean,
		preventRedirect: boolean,
		onProgress?: (e: ProgressEvent) => void,
		requestId?: string,
	): Promise<void> {
		const { status, responseText } = xhr;
		const isInvalidCredentials = responseText.includes('INVALID_CREDENTIALS');
		const isAuthenticationFailure = responseText.includes('AUTHENTICATION_FAILURE');
		const { multiAppAuthenticationInfos: { isAnotherUserLogin } } = store.getState();
		const isMobileApp = UserAgentUtils.isMobileApp;

		if (isInvalidCredentials || isAnotherUserLogin || isAuthenticationFailure) {
			return reject(xhr);
		}

		const location = xhr.getResponseHeader(HttpHeaderType.LOCATION);
		const isRedirectToExternalLoginPage = status === ServerStatusCode.FORBIDDEN && location && location !== '#';

		if (isRedirectToExternalLoginPage && !isMobileApp) {
			// display external login page
			// after succesfull login this page will redirect back to current page
			const { location: { origin, href: currentRoute } } = window;
			let domain = origin;

			if (origin.includes('localhost')) { // for dev only
				const {
					galleryInfos: {
						config: {
							service: { host = 'https://migrationv3.zone-secure.net' } = {},
						},
					},
				} = store.getState();
				domain = Utils.getUrlWithHttpsProtocol(host);
			} else if (location && location.includes('http')) {
				domain = '';
			}

			const loginUrl = `${domain}${location}&redirectTo=${encodeURIComponent(currentRoute)}`;
			window.location.href = loginUrl;
		} else {
			if (attempt <= MAX_RETRIES) {
				const recallForbiddenRequest = async () => {
					const { token } = ApiUtils;
					const urlWithToken = url.includes('token=')
						? url.replace(/(token=)[^\&]+/, '$1' + token)
						: `${url}${Utils.getUrlSeparator(url)}token=${token}`;

					const response = await Utils.promiseXHR(
						protocol,
						urlWithToken,
						headers,
						isAsync,
						dataInUrl,
						dataInBody,
						attempt + 1,
						ignoreConnectionStatus,
						withCredentials,
						preventRedirect,
						onProgress,
						requestId,
					);

					resolve(response);
				};

				if (Utils.isFreeUrlApp()) {
					await store.dispatch(freeUrlAppLogin());
					await recallForbiddenRequest();
					return;
				}

				const credentials = Utils.getMultiAppCredentials();
				const isLoggedIn = credentials && credentials.login;

				if (isLoggedIn) {
					const { method, company } = credentials as MultiAppAuthenticationCredentials;
					if (method === AuthMethod.Webpublication) {
						await store.dispatch(checkActiveSession(true));
						await recallForbiddenRequest();
					} else {
						const { galleryInfos: { config } } = store.getState();
						await store.dispatch(orgLogin(config, company));
						await recallForbiddenRequest();
					}
				}
			} else {
				reject(xhr);
			}
		}
	}

	static dataToUrl(data: {}, mustEncode: boolean = true): string {
		let url = '';

		for (let i in data) {
			if (data[i] instanceof Array) {
				for (let j = 0; j < data[i].length; j++) {
					const key = mustEncode ? encodeURIComponent(`${i}`) : `${i}`;
					const value = mustEncode ? encodeURIComponent(data[i][j]) : data[i][j];
					url += `&${key}=${value}`;
				}
			} else {
				if (url) {
					url += '&';
				}
				url += `${i}=${mustEncode ? encodeURIComponent(data[i]) : data[i]}`;
			}

		}

		return url;
	}

	static fetchJson<T>(
		url: string,
		protocol: string = 'GET',
		isAsync: boolean = true,
		ignoreConnectionStatus: boolean = false,
		dataInUrl: {} | null = null,
		dataInBody: BodyInit | undefined = undefined,
		contentType: string | undefined = undefined,
		withCredentials: boolean = true,
		preventRedirect: boolean = true,
		preventSecurityHeader: boolean = false,
		onProgress?: (e: ProgressEvent) => void,
		requestId?: string,
	): Promise<T> {
		return Utils.fetchText(
			url, protocol, isAsync, ignoreConnectionStatus, dataInUrl, dataInBody, contentType, withCredentials, preventRedirect, preventSecurityHeader, onProgress, requestId
		)
			.then(responseText => responseText && JSON.parse(responseText));
	}

	static encodeFormPayload(fields: {}) {
		const fieldKeys = Object.keys(fields);
		const uriParameters: Array<string> = [];
		fieldKeys.forEach(fieldKey => {
			const encodedKey = encodeURIComponent(fieldKey);
			const value = fields[fieldKey];
			const encodedValues = Array.isArray(value)
				? value.map((value) => encodeURIComponent(String(value)))
				: [encodeURIComponent(fields[fieldKey])];

			uriParameters.push(
				...encodedValues.map(encodedValue => `${encodedKey}=${encodedValue}`)
			);
		});

		return uriParameters.join('&');
	}

	static fetchText(
		url: string,
		protocol: string = 'GET',
		isAsync: boolean = true,
		ignoreConnectionStatus: boolean = false,
		dataInUrl: {} | null = null,
		dataInBody: BodyInit | undefined = undefined,
		contentType: string | undefined = undefined,
		withCredentials: boolean = true,
		preventRedirect: boolean = true,
		preventSecurityHeader: boolean = false,
		onProgress?: (e: ProgressEvent) => void,
		requestId?: string,
	): | Promise<string> {
		const headers = ApiUtils.getSecurityHeaderIfNeeded(preventSecurityHeader);

		if (contentType) {
			headers.push({
				key: HttpHeaderType.CONTENT_TYPE,
				value: contentType,
			});
		}

		return Utils.promiseXHR(protocol, url, headers, isAsync, dataInUrl, dataInBody, undefined, ignoreConnectionStatus, withCredentials, preventRedirect, onProgress, requestId)
			.then(response => {
				return [
					0,
					ServerStatusCode.OK,
					ServerStatusCode.NO_CONTENT,
				].includes(response.status)
					? Promise.resolve(response.responseText)
					: Promise.reject(response);
			})
			.catch(error => {
				return Promise.reject(error);
			});
	}

	static isResourceAvailable(url: string): Promise<XMLHttpRequest> {
		return new Promise<XMLHttpRequest>((resolve, reject) => {
			const xhr = new XMLHttpRequest();
			xhr.timeout = 1000;
			xhr.open('GET', url, true);

			xhr.onload = () => {
				if (xhr.status === 200) {
					resolve(xhr);
				} else {
					reject(xhr);
				}
			};

			xhr.ontimeout = () => {
				if (xhr.status === 0) {
					resolve(xhr);
				} else {
					reject(xhr);
				}
			};

			xhr.send();
		});
	}

	static parseDate(dateString: string): Date {
		const dateStringSplit = dateString.split(' ');
		const dateSplit = dateStringSplit[0].split('-').map(e => parseInt(e, 10));
		const timeSplit = dateStringSplit[1].split(':').map(e => parseInt(e, 10));
		return new Date(dateSplit[0], dateSplit[1] - 1, dateSplit[2], timeSplit[0], timeSplit[1], timeSplit[2]);
	}

	static stringIsInStringEnum(value: string, actionKeys: {}) {
		for (let k in actionKeys) {
			if (actionKeys.hasOwnProperty(k) && actionKeys[k] === value) {
				return true;
			}
		}

		return false;
	}

	static getUrlWithCorrectProtocol(url: string) {
		const isMobileApp = UserAgentUtils.isMobileApp;
		const isHttpsNeeded = isMobileApp || window.location.protocol === 'https:';
		const newProtocol = isHttpsNeeded ? 'https' : 'http';

		const urlProtocolRegex = /^(.+):\/\//;
		const regexMatch = urlProtocolRegex.exec(url);

		if (regexMatch === null) {
			return `${newProtocol}://${url}`;
		}

		return url.replace(regexMatch[1], newProtocol );
	}

	static getUrlSeparator(url: string): string {
		return url.includes('?') ? '&' : '?';
	}

	static getUrlWithHttpsProtocol(url: string): string {
		return url.replace('http:', 'https:');
	}

	static isSameUrl(url1: string, url2: string): boolean {
		const getUrlWithEndSlash = (url: string): string => (
			url[url.length - 1] === '/' ? url : url + '/'
		);

		return Utils.getUrlWithHttpsProtocol(getUrlWithEndSlash(url1)) === Utils.getUrlWithHttpsProtocol(getUrlWithEndSlash(url2));
	}

	static getUrlForSecuredResource(url: string): string {
		const { appInfos } = store.getState();
		if (!Utils.isEmbedMode() && (!Utils.isMultiApp(appInfos) || !ApiUtils.token)) {
			return url;
		}

		const urlWithToken = url + `${Utils.getUrlSeparator(url)}token=${ApiUtils.token}`;
		return Utils.getUrlWithHttpsProtocol(urlWithToken);
	}

	static getFileExtension(fileName?: string): string {
		if (fileName) {
			const extensionRegex = /(?:\.([^.]+))?(?:\?.*)?$/;
			const regexMatch = extensionRegex.exec(fileName);
			return !!regexMatch && !!regexMatch[1]
				? regexMatch[1].split('?')[0] // remove query params
				: '';
		}
		return '';
	}

	static getScreenFormat(): ScreenFormat {
		if (UserAgentUtils.isMobileApp && UserAgentUtils.isPhone()) {
			return ScreenFormat.Mobile;
		} else if (UserAgentUtils.isMobileApp && UserAgentUtils.isTablet()) {
			return ScreenFormat.Tablet;
		} else {
			const screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

			if (screenWidth < 768) {
				return ScreenFormat.Mobile;
			} else if (screenWidth < 1200) {
				return ScreenFormat.Tablet;
			}
			return ScreenFormat.Desktop;
		}
	}

	public static generateId = (length: number = 15): string => {
		let text = '';
		let possibleChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

		for (let i = 0; i < length; i++) {
			text += possibleChars.charAt(Math.floor(Math.random() * possibleChars.length));
		}
		return text;
	}

	static getCookie(cookieName: string): string {
		const cookieList = document.cookie.split(';');
		const key = `${cookieName}=`;
		const selectedCookie = cookieList.find(element => element.includes(cookieName));

		return selectedCookie ? selectedCookie.substring(key.length) : '';
	}

	static setCookie(cookieName: string, value: string, expireDays: number): void {
		const expireDate = new Date();
		expireDate.setTime(expireDate.getTime() + (expireDays * 24 * 60 * 60 * 1000));
		document.cookie = `${cookieName}=${value};expires=${expireDate.toUTCString()}`;
	}

	static isSecure = (appType: AppType, needCredentials: boolean): boolean => {
		return appType === AppType.Multiple || needCredentials;
	}

	static isMultiApp = (appInfos: AppInfosState): boolean => {
		return appInfos.type === AppType.Multiple;
	}

	static isMobileMultiApp = (appInfos: AppInfosState): boolean => {
		return UserAgentUtils.isMobileApp && Utils.isMultiApp(appInfos);
	}

	static isReadOnlyApp = (userRole: Api.CustomerContextRoles): boolean => {
		return [
			Api.CustomerContextRoles.ROLE_APPLICATION_VIEWER,
			Api.CustomerContextRoles.ROLE_LIBRARY_ACCOUNT_MANAGER,
		].includes(userRole);
	}

	static isMenuItemTypeRoute = (itemType: MenuItemType): boolean => {
		const nonRouteTypes = [
			MenuItemType.Asset,
			MenuItemType.Link,
			MenuItemType.Folder,
			MenuItemType.Page,
			MenuItemType.SearchButton,
			MenuItemType.UpdateFolderTreeButton,
		];
		return !nonRouteTypes.includes(itemType);
	}

	static uncapitalize = (captilizedString: string): string => {
		return `${captilizedString.charAt(0).toLowerCase()}${captilizedString.substring(1)}`;
	}

	static getCurrentFolderId = (galleryId: number, routerParamsHistory: RouterParamsState[] ): number | undefined => {
		const currentRoute = routerParamsHistory[routerParamsHistory.length - 1];
		const { params } = currentRoute;
		const { origin, originParam } = params;
		if (origin === DriveItemListOrigin.Folder) {
			return originParam ? parseInt(originParam, 10) : galleryId;
		}
		return;
	}

	static getGalleryMeta = (driveList: DriveListState, galleryConfig: Api.Config): DriveItemInfo => {
		const { driveItems } = driveList;
		const galleryId = galleryConfig.applicationGallery;
		return driveItems[galleryId];
	}

	static getPrivateDirId = (driveList: DriveListState, galleryConfig: Api.Config): number | undefined => {
		const galleryMeta = Utils.getGalleryMeta(driveList, galleryConfig);
		return galleryMeta ? galleryMeta.privateDirId : undefined;
	}

	static getSharedDirId = (driveList: DriveListState, galleryConfig: Api.Config): number | undefined => {
		const galleryMeta = Utils.getGalleryMeta(driveList, galleryConfig);
		return galleryMeta ? galleryMeta.sharedDirId : undefined;
	}

	static getArchiveDirId = (driveList: DriveListState, galleryConfig: Api.Config): number | undefined => {
		const galleryMeta = Utils.getGalleryMeta(driveList, galleryConfig);
		return galleryMeta ? galleryMeta.archiveDirId : undefined;
	}

	static getSourcesDirId = (driveList: DriveListState, galleryConfig: Api.Config): number | undefined => {
		const galleryMeta = Utils.getGalleryMeta(driveList, galleryConfig);
		return galleryMeta ? galleryMeta.sourcesDirId : undefined;
	}

	static getFolderIdForNewResource = (): number | undefined => {
		const {
			galleryInfos: { config },
			routerParamsHistory,
			driveList,
		} = store.getState();
		const isAccountManagerOrAdmin = getIsAccountManagerOrAdmin(store.getState());
		const currentFolderId = Utils.getCurrentFolderId(config.applicationGallery, routerParamsHistory);

		if (!isAccountManagerOrAdmin) {
			if (DriveItemUtils.isFolderInEditorExtendedEditableScope(currentFolderId, store.getState())) {
				return currentFolderId;
			}
			return Utils.getFolderIdForNewPrivateResource();
		}

		const sharedDirId = Utils.getSharedDirId(driveList, config);
		const archiveDirId = Utils.getArchiveDirId(driveList, config);
		const forbiddenFolderIds = [sharedDirId, archiveDirId];

		if (!currentFolderId || forbiddenFolderIds.includes(currentFolderId)) {
			return Utils.getPrivateDirId(driveList, config);
		}

		return currentFolderId;
	}

	static getFolderIdForNewPrivateResource = (): number | undefined => {
		const {
			galleryInfos: { config },
			routerParamsHistory,
			driveList,
		} = store.getState();

		const currentFolderId = Utils.getCurrentFolderId(config.applicationGallery, routerParamsHistory);
		const privateDirId = Utils.getPrivateDirId(driveList, config);
		const isPrivateDirDescendant = DriveItemUtils.isDirDescendantOf(currentFolderId, privateDirId, driveList.driveItems);

		return currentFolderId && isPrivateDirDescendant
			? currentFolderId
			: privateDirId;
	}

	static getViewportHeight = () => Math.max(
		document.documentElement.clientHeight,
		window.innerHeight || 0
	)

	// This static property uses getViewportHeight to get its value and must be declared after
	// tslint:disable-next-line
	static initialViewportHeight = Utils.getViewportHeight();

	static getKeyboardHeight = () => Utils.getViewportHeight() - Utils.initialViewportHeight;

	static itemsToById = (items: {}[], idKey: string = 'id') => (
		items.reduce((byId, item) => {
			byId[item[idKey]] = item;
			return byId;
		}, {})
	)

	static cancelRequest(requestId: string) {
		const request = Utils.cancelableRequests[requestId];
		if (request) {
			request.abort();
		}
	}

	static getCssClasses(classes: string, conditionalClasses: { [k: string]: boolean }): string {
		return Object.keys(conditionalClasses).reduce((acc, current) => {
			acc += conditionalClasses[current] ? ` ${current}` : '';
			return acc;
		}, classes);
	}

	static isEmptyObject(obj: Object): boolean {
		for (let prop in obj) {
			if (obj.hasOwnProperty(prop)) {
				return false;
			}
		}

		return true;
	}

	// for use gallApp in iframe in external projects (e.g., backoffice)
	static isEmbedMode(): boolean {
		return location.href.includes('?embed=true');
	}

	static isEmptyPlaylistMode(): boolean {
		return location.href.includes('isEmptyPlaylist=true');
	}

	static isPlaylistMode(): boolean {
		return location.href.includes('isPlaylist=true');
	}

	static isAccessibleVersionMode(): boolean {
		return location.href.includes('isAccessibleVersion=true');
	}

	static isFreeUrlApp(): boolean {
		return location.href.includes('wpAccessToken');
	}

	static isOpenedFromAdmin(): boolean {
		return location.href.includes('token') && !Utils.isFreeUrlApp();
	}

	static getQueryParamFromUrl(param: string): string {
		const { search } = location;
		const paramsStringStart = search.indexOf('?') + 1;
		const paramsStringEnd = search.includes('/') ? search.indexOf('/') : undefined;
		const paramsString = search.substring(paramsStringStart, paramsStringEnd);
		const params = paramsString
			.split('&')
			.reduce((acc, current) => {
				const [name, value] = current.split('=');
				acc[name] = value;
				return acc;
			}, {});

		return params[param];
	}

	static isSharedNote(id: number): boolean {
		return id > 0;
	}

	static isDriveItemFetched(id: number): boolean {
		const {
			galleryInfos: { config },
			driveList,
			driveList: { driveItemsFetchStatus },
		} = store.getState();

		const archiveDirId = Utils.getArchiveDirId(driveList, config);
		const isArchiveDir = id === archiveDirId;

		if ((UserAgentUtils.isMobileApp || Utils.isMaster(config)) && !isArchiveDir) {
			return true;
		}

		return driveItemsFetchStatus[id] && driveItemsFetchStatus[id].metaFetchStatus === FetchStatus.FETCHED;
	}

	static isGalleryMetaFetched(): boolean {
		const {
			galleryInfos: {
				config: { applicationGallery },
			},
		} = store.getState();

		return Utils.isDriveItemFetched(applicationGallery);
	}

	static getGalleryPrefix(int: number, login?: string): string {
		return login ? `${int}-${login}` : int.toString();
	}

	static shouldGetProfileData(): boolean {
		const {
			appInfos,
			profile: { clientId },
		} = store.getState();
		const isProfileLoaded = !!clientId;

		return (Utils.isMultiApp(appInfos) || Utils.isEmbedMode()) && !isProfileLoaded;
	}

	static isShareOnlyViaUprezEnabled(sharedNetworks?: Api.ShareNetwork[]): boolean {
		return !sharedNetworks ||
			(sharedNetworks.length === 1 && sharedNetworks[0] === Api.ShareNetwork.Uprez);
	}

	static isShareOnlyViaCopyEnabled(sharedNetworks?: Api.ShareNetwork[]): boolean {
		return !!sharedNetworks &&
			sharedNetworks.length === 1 && sharedNetworks[0] === Api.ShareNetwork.Copy;
	}

	static isMultipleShareEnabled(sharedNetworks?: Api.ShareNetwork[]): boolean {
		return !!sharedNetworks && sharedNetworks.length > 1;
	}

	static isNumber(value: string | number): boolean {
		return Number(value) >= 0;
	}

	private static handleNativeApiBug(
		xhr: XMLHttpRequest,
		resolve: (value?: XMLHttpRequest | PromiseLike<XMLHttpRequest> | undefined) => void,
		reject: (reason?: {}) => void,
		protocol: string,
		url: string,
		headers: HttpHeader[],
		isAsync: boolean,
		dataInUrl: {} | null,
		dataInBody: BodyInit | undefined,
		attempt: number,
		ignoreConnectionStatus: boolean,
		withCredentials: boolean,
		preventRedirect: boolean,
		onProgress?: (e: ProgressEvent) => void,
		requestId?: string,
	) {
		if (attempt < MAX_RETRIES) {
			Utils.promiseXHR(protocol, url, headers, isAsync, dataInUrl, dataInBody, attempt + 1, ignoreConnectionStatus, withCredentials, preventRedirect, onProgress, requestId)
				.then(res => resolve(res))
				.catch(res => reject(res));
		} else {
			reject(xhr);
		}
	}
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/index.ts